import { fft, hannWindow, magnitudeSpectrum } from '../../core/dsp/Fft.js';

/**
 * What the music is doing, worked out once, ahead of time.
 *
 * Phase 4 re-derived the band levels from the audio that happened to be playing.
 * That is right for a live replay and impossible offline: there is no audio
 * playing, and there must not be — a renderer that needs a song to be audible
 * cannot run faster than real time, in a background tab, or twice with the same
 * result. So the whole song is analysed up front into a timeline that any frame
 * time can be sampled from.
 *
 * The timeline is stored at its own rate, not at the render's. A 100 Hz analysis
 * feeding a 60 fps render is not a mismatch to be avoided — it is the point.
 * Analysis resolution is a property of the *music*, frame rate is a property of
 * the *output*, and tying them together would mean a 30 fps render heard a
 * different song from a 60 fps one.
 */

/** The analysis contract. Bump when the numbers this produces would change. */
export const AUDIO_ANALYSIS_VERSION = 1;

/** Band edges in Hz, matching the live analyser so the two agree. */
const BASS_MAX_HZ = 250;
const MID_MAX_HZ = 2000;

export interface AudioFeatureSample {
  readonly amplitude: number;
  readonly bass: number;
  readonly mid: number;
  readonly treble: number;
  readonly onset: number;
}

export const SILENT_SAMPLE: AudioFeatureSample = {
  amplitude: 0,
  bass: 0,
  mid: 0,
  treble: 0,
  onset: 0,
};

export interface AudioAnalysisOptions {
  /** FFT size. Power of two. */
  readonly windowSize?: number;
  /** Samples between analysis frames. Determines the timeline's rate. */
  readonly hopSize?: number;
}

export interface AudioFeatureTimelineData {
  readonly version: number;
  readonly sampleRate: number;
  readonly windowSize: number;
  readonly hopSize: number;
  /** Seconds between consecutive samples. */
  readonly step: number;
  readonly durationSeconds: number;
  readonly amplitude: Float32Array;
  readonly bass: Float32Array;
  readonly mid: Float32Array;
  readonly treble: Float32Array;
  /** Onset strength per hop; zero where no transient was detected. */
  readonly onset: Float32Array;
}

/**
 * A sampled analysis, readable at any time.
 *
 * Continuous bands are interpolated between hops so a 60 fps render does not
 * see the analysis rate as a staircase. Onsets are *not* interpolated: an onset
 * is a moment, and half an onset is not a thing. They are consumed by asking
 * which hops fall inside a frame's span, which is what `onsetsBetween` is for.
 */
export class AudioFeatureTimeline {
  constructor(readonly data: AudioFeatureTimelineData) {}

  get durationSeconds(): number {
    return this.data.durationSeconds;
  }

  get sampleCount(): number {
    return this.data.amplitude.length;
  }

  /** Linearly interpolated bands at `time`. */
  sample(time: number): AudioFeatureSample {
    const { amplitude, bass, mid, treble, step } = this.data;
    const count = amplitude.length;
    if (count === 0) return SILENT_SAMPLE;
    if (!Number.isFinite(time) || time <= 0) {
      return {
        amplitude: amplitude[0] as number,
        bass: bass[0] as number,
        mid: mid[0] as number,
        treble: treble[0] as number,
        onset: 0,
      };
    }

    const position = time / step;
    const lower = Math.floor(position);
    if (lower >= count - 1) {
      const last = count - 1;
      return {
        amplitude: amplitude[last] as number,
        bass: bass[last] as number,
        mid: mid[last] as number,
        treble: treble[last] as number,
        onset: 0,
      };
    }
    const blend = position - lower;
    const mix = (values: Float32Array): number =>
      (values[lower] as number) * (1 - blend) + (values[lower + 1] as number) * blend;

    return {
      amplitude: mix(amplitude),
      bass: mix(bass),
      mid: mix(mid),
      treble: mix(treble),
      onset: 0,
    };
  }

  /**
   * The strongest onset in `(from, to]`, or zero.
   *
   * A frame covers a span of song time, and at 30 fps that span holds three
   * analysis hops. Taking the strongest rather than the first means a frame
   * reports the transient that actually defines it, and taking the span rather
   * than the instant means no onset is ever skipped however low the frame rate
   * — which is the offline half of the frame-gap rule.
   */
  onsetsBetween(from: number, to: number): number {
    const { onset, step } = this.data;
    if (onset.length === 0 || to <= from) return 0;
    const first = Math.max(0, Math.ceil(from / step));
    const last = Math.min(onset.length - 1, Math.floor(to / step));
    let strongest = 0;
    for (let index = first; index <= last; index += 1) {
      const value = onset[index] as number;
      if (value > strongest) strongest = value;
    }
    return strongest;
  }

  /** How many onsets fall in a span. Used by the tests and diagnostics. */
  onsetCountBetween(from: number, to: number): number {
    const { onset, step } = this.data;
    if (onset.length === 0 || to <= from) return 0;
    const first = Math.max(0, Math.ceil(from / step));
    const last = Math.min(onset.length - 1, Math.floor(to / step));
    let count = 0;
    for (let index = first; index <= last; index += 1) {
      if ((onset[index] as number) > 0) count += 1;
    }
    return count;
  }
}

/**
 * Analyses raw mono PCM into a feature timeline.
 *
 * Pure: samples in, numbers out, no audio context and no clock. That is what
 * lets it run in a test, in a worker, or on a machine with no sound card, and
 * what makes two runs of the same file produce identical output.
 */
export function analysePcm(
  samples: Float32Array,
  sampleRate: number,
  options: AudioAnalysisOptions = {},
): AudioFeatureTimeline {
  const windowSize = options.windowSize ?? 2048;
  const hopSize = options.hopSize ?? Math.max(1, Math.round(sampleRate / 100)); // ~100 Hz
  const step = hopSize / sampleRate;

  const hops = samples.length < windowSize ? 0 : Math.floor((samples.length - windowSize) / hopSize) + 1;
  const amplitude = new Float32Array(hops);
  const bass = new Float32Array(hops);
  const mid = new Float32Array(hops);
  const treble = new Float32Array(hops);
  const onset = new Float32Array(hops);

  if (hops === 0) {
    return new AudioFeatureTimeline({
      version: AUDIO_ANALYSIS_VERSION,
      sampleRate,
      windowSize,
      hopSize,
      step,
      durationSeconds: samples.length / sampleRate,
      amplitude,
      bass,
      mid,
      treble,
      onset,
    });
  }

  const bins = windowSize / 2;
  const hzPerBin = sampleRate / windowSize;
  const bassEnd = Math.max(1, Math.min(bins, Math.round(BASS_MAX_HZ / hzPerBin)));
  const midEnd = Math.max(bassEnd + 1, Math.min(bins, Math.round(MID_MAX_HZ / hzPerBin)));

  const window = hannWindow(windowSize);
  const real = new Float32Array(windowSize);
  const imag = new Float32Array(windowSize);
  const spectrum = new Float32Array(bins);
  const previous = new Float32Array(bins);

  // Normalisation so a full-scale sine reads near 1 rather than near `windowSize`.
  const scale = 2 / (windowSize * 0.5);
  let fluxAverage = 0;

  for (let hop = 0; hop < hops; hop += 1) {
    const offset = hop * hopSize;
    for (let index = 0; index < windowSize; index += 1) {
      real[index] = (samples[offset + index] as number) * (window[index] as number);
      imag[index] = 0;
    }
    fft(real, imag);
    magnitudeSpectrum(real, imag, spectrum);

    let bassSum = 0;
    let midSum = 0;
    let trebleSum = 0;
    let flux = 0;

    for (let bin = 0; bin < bins; bin += 1) {
      const value = Math.min(1, (spectrum[bin] as number) * scale);
      if (bin < bassEnd) bassSum += value;
      else if (bin < midEnd) midSum += value;
      else trebleSum += value;

      // Only rises count: a transient is energy appearing.
      const rise = value - (previous[bin] as number);
      if (rise > 0) flux += rise;
      previous[bin] = value;
    }

    const trebleBins = Math.max(1, bins - midEnd);
    bass[hop] = clamp01(bassSum / bassEnd);
    mid[hop] = clamp01(midSum / (midEnd - bassEnd));
    treble[hop] = clamp01(trebleSum / trebleBins);
    amplitude[hop] = clamp01((bassSum + midSum + trebleSum) / bins);

    // Compared against its own recent average, so a quiet passage still
    // produces onsets and a loud one is not permanently triggering.
    const threshold = fluxAverage * 1.5 + 0.02;
    onset[hop] = flux > threshold ? clamp01((flux - threshold) / (threshold + 1)) : 0;
    fluxAverage += (flux - fluxAverage) * 0.12;
  }

  return new AudioFeatureTimeline({
    version: AUDIO_ANALYSIS_VERSION,
    sampleRate,
    windowSize,
    hopSize,
    step,
    durationSeconds: samples.length / sampleRate,
    amplitude,
    bass,
    mid,
    treble,
    onset,
  });
}

/**
 * Identifies an analysis so it can be reused.
 *
 * Every input that changes the numbers is in the key: which song, which audio,
 * which analysis code, and the two window parameters. Change any of them and
 * the key changes, so a stale timeline can never be served — that is the entire
 * invalidation rule, and it is why `AUDIO_ANALYSIS_VERSION` must be bumped when
 * the maths changes even though nothing else did.
 */
export function featureCacheKey(input: {
  readonly songId: string;
  readonly audioHash: string;
  readonly windowSize: number;
  readonly hopSize: number;
}): string {
  return [
    input.songId,
    input.audioHash,
    `v${AUDIO_ANALYSIS_VERSION}`,
    `w${input.windowSize}`,
    `h${input.hopSize}`,
  ].join(':');
}

/**
 * A hash of the audio itself, over every sample.
 *
 * An earlier version sampled every 1009th value on the reasoning that a full
 * pass was too expensive. It is not, and it was not safe: a strided hash cannot
 * see an edit that falls between its probes, so two genuinely different pieces
 * of audio could share a key and be served each other's analysis. A full pass
 * over ten million samples costs a few tens of milliseconds — against the one
 * to three seconds of FFT work it exists to avoid, that is not a trade worth
 * making for correctness.
 */
export function audioHash(samples: Float32Array, sampleRate: number): string {
  let hash = 0x811c9dc5 ^ (samples.length | 0) ^ (sampleRate | 0);
  for (let index = 0; index < samples.length; index += 1) {
    // Quantised to 16 bits: below that is dither, and float noise should not
    // invalidate a cache entry for audio that is bit-identical after decoding.
    hash ^= Math.round((samples[index] as number) * 32767) | 0;
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) | 0;
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Bounded store keyed by `featureCacheKey`. */
export class AudioFeatureCache {
  private readonly entries = new Map<string, AudioFeatureTimeline>();
  private hits = 0;
  private misses = 0;

  constructor(private readonly capacity = 4) {}

  get(key: string): AudioFeatureTimeline | null {
    const found = this.entries.get(key);
    if (found === undefined) {
      this.misses += 1;
      return null;
    }
    // Re-inserted so the most recently used survives eviction.
    this.entries.delete(key);
    this.entries.set(key, found);
    this.hits += 1;
    return found;
  }

  set(key: string, timeline: AudioFeatureTimeline): void {
    this.entries.set(key, timeline);
    while (this.entries.size > this.capacity) {
      const oldest = this.entries.keys().next().value;
      if (oldest === undefined) break;
      this.entries.delete(oldest);
    }
  }

  clear(): void {
    this.entries.clear();
  }

  diagnostics(): {
    readonly size: number;
    readonly capacity: number;
    readonly hits: number;
    readonly misses: number;
    readonly keys: readonly string[];
  } {
    return {
      size: this.entries.size,
      capacity: this.capacity,
      hits: this.hits,
      misses: this.misses,
      keys: [...this.entries.keys()],
    };
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
